
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Star } from 'lucide-react';

interface StoreProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  discount_price?: number;
  images: string[];
  design_image_url?: string;
  store_name: string;
  category: string;
  available_colors: string[];
  available_sizes: string[];
  print_options: string[];
  mockup_images: Record<string, Record<string, string>>;
}

interface StoreProductCardProps {
  product: StoreProduct;
  onProductClick: (product: StoreProduct) => void;
}

export const StoreProductCard: React.FC<StoreProductCardProps> = ({ 
  product, 
  onProductClick 
}) => {
  const discountPercentage = product.discount_price 
    ? Math.round(((product.price - product.discount_price) / product.price) * 100)
    : 0;

  return (
    <Card className="overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow">
      <div 
        className="aspect-square relative overflow-hidden"
        onClick={() => onProductClick(product)}
      >
        <img 
          src={product.images[0]} 
          alt={product.title} 
          className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Design overlay */}
        {product.design_image_url && (
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={product.design_image_url}
              alt="Design aplicado"
              className="max-w-[35%] max-h-[35%] object-contain filter drop-shadow-md"
            />
          </div>
        )}
        
        {product.discount_price && (
          <Badge className="absolute top-2 right-2 bg-red-500 hover:bg-red-600">
            -{discountPercentage}%
          </Badge>
        )}
      </div>
      
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm truncate">{product.title}</h3>
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs">4.8</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          por {product.store_name}
        </p>
      </CardHeader>
      
      <CardContent className="p-4 pt-0">
        <div className="flex items-center justify-between">
          <div>
            {product.discount_price ? (
              <div className="flex items-center gap-2">
                <span className="font-bold text-green-600">
                  R$ {product.discount_price.toFixed(2)}
                </span>
                <span className="text-xs text-muted-foreground line-through">
                  R$ {product.price.toFixed(2)}
                </span>
              </div>
            ) : (
              <span className="font-bold text-green-600">
                R$ {product.price.toFixed(2)}
              </span>
            )}
          </div>
          
          <Button 
            size="sm" 
            className="rounded-full h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onProductClick(product);
            }}
          >
            <ShoppingCart className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
